import React, { useState } from 'react';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExchangeAlt, faChevronDown, faChevronUp, faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { formatCurrency } from '../../utils/currencyFormatter';
import { calculatePackageTotals } from './pricingUtils';

const CompareDetailsAccordion = ({ estimateData }) => {
  const [isOpen, setIsOpen] = useState(false);

  const packages = estimateData?.packages;
  if (!packages || packages.length < 2) return null;

  // We only compare the first two for a clean mobile UI, or all if we want to map horizontally
  const comparePkgs = packages.slice(0, 2);
  const currency = estimateData.currency;
  const formatCost = (amount) => formatCurrency(amount, currency);

  const getTransportTotal = (pkg) => calculatePackageTotals(pkg, estimateData).transportAmount;
  const getGrandTotal = (pkg) => calculatePackageTotals(pkg, estimateData).grandTotalMid;

  return (
    <div className="border border-gray-200 rounded-lg bg-white mb-6 shadow-sm overflow-hidden mt-6">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between focus:outline-none"
      >
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faExchangeAlt} className="text-gray-500" />
          <span className="font-bold text-gray-800 text-sm">Compare Details</span>
        </div>
        <FontAwesomeIcon icon={isOpen ? faChevronUp : faChevronDown} className="text-gray-400 text-sm" />
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 p-4">
          
          {/* Compare Table Headers */}
          <div className="flex justify-between mb-4 border-b border-gray-100 pb-2">
            <div className="w-1/3"></div>
            {comparePkgs.map((pkg, idx) => (
              <div key={idx} className={`w-1/3 text-right text-[10px] font-bold uppercase tracking-wider ${idx === 0 ? 'text-[#0F826B]' : 'text-[#104A7D]'}`}>
                {pkg.name}
              </div>
            ))}
          </div>

          {/* Row: Hotels & Meals */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-1/3 text-[10px] font-bold text-gray-800 uppercase tracking-wider">Hotels & Meals</div>
            {comparePkgs.map((pkg, idx) => (
              <div key={idx} className="w-1/3 text-right text-xs text-gray-600">
                {formatCost(pkg.breakdown?.hotelMeal)}
              </div>
            ))}
          </div>

          {/* Row: Local Transfers */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-1/3 text-[10px] font-bold text-gray-800 uppercase tracking-wider">Local Transfers</div>
            {comparePkgs.map((pkg, idx) => (
              <div key={idx} className="w-1/3 text-right text-xs text-gray-600">
                {formatCost(pkg.breakdown?.transfers)}
              </div>
            ))}
          </div>

          {/* Row: Activities */}
          <div className="flex justify-between items-center mb-4">
            <div className="w-1/3 text-[10px] font-bold text-gray-800 uppercase tracking-wider">Activities</div>
            {comparePkgs.map((pkg, idx) => (
              <div key={idx} className="w-1/3 text-right text-xs text-gray-600">
                {formatCost(pkg.breakdown?.activities)}
              </div>
            ))}
          </div>

          {/* Row: Transport Total */}
          {estimateData?.includeTransport !== false && (
            <div className="flex justify-between items-center mb-4">
              <div className="w-1/3 text-[10px] font-bold text-gray-800 uppercase tracking-wider">Transport Total</div>
              {comparePkgs.map((pkg, idx) => (
                <div key={idx} className="w-1/3 text-right text-xs text-gray-600">
                  {formatCost(getTransportTotal(pkg))}
                </div>
              ))}
            </div>
          )}

          {/* Row: Total Package Price */}
          <div className="flex justify-between items-center mt-5 pt-3 border-t border-gray-100">
            <div className="w-1/3 text-[10px] font-bold text-gray-800 uppercase tracking-wider">Total Package Price</div>
            {comparePkgs.map((pkg, idx) => (
              <div key={idx} className={`w-1/3 text-right text-sm font-bold ${idx === 0 ? 'text-[#0F826B]' : 'text-[#104A7D]'}`}>
                {formatCost(getGrandTotal(pkg))}
              </div>
            ))}
          </div>

        </div>
      )}
      
      {/* Footer Info */}
      <div className="bg-[#F0F5FA] p-3 m-4 mt-0 rounded border border-[#E1EAF4] flex items-start gap-2">
        <FontAwesomeIcon icon={faInfoCircle} className="text-[#A2B8CC] mt-0.5 text-[10px]" />
        <p className="text-[9px] text-[#7893AB] leading-relaxed">
          Prices shown are estimated package costs and may vary depending on seasonality, hotel availability, airfare fluctuations, and final itinerary confirmation.
        </p>
      </div>
    </div>
  );
};

export default CompareDetailsAccordion;
